import { defineComponent as _defineComponent } from 'vue'
import {computed, ComputedRef, onMounted, onUnmounted, reactive, watch} from "vue";
  import MonthPicker from "@/components/MonthPicker.vue";
  import Order from "@/model/Order";
  import SwitchView from "@/components/SwitchView.vue";
  import OrderPerDiemsDate from "./OrderPerDiemsDate.vue";
  import {OrderPerDiemDateEmployeeEntry, OrderPerDiemDateEntry} from "@/service/OrderPerDiemMonth";
  import moment from "moment/moment";
  import {date, orderId} from "@/RouterUtils";
  import {isValidDate, isValidMonth} from "@/DateUtils";
  import router from "@/router";
  import eventBus from "@/eventbus";
  import {MessageTypes} from "@/components/message-types";
  import OrderServiceV2, {OrderPerDiemDayDto} from "@/service/OrderServiceV2";
  import EmployeeName from "@/model/EmployeeName";
  import TimeSpent from "@/model/TimeSpent";
  import PerDiem from "@/model/PerDiem";

  declare interface BreadCrumb {
    text: string,
    disabled: boolean
  }

  
export default /*#__PURE__*/_defineComponent({
  __name: 'OrderPerDiems',
  props: {
    order: {
      type: Order,
      default: () => null
    }
  },
  setup(__props) {

const props = __props

  

  const initialState = {
    perDiemOrderDates: new Array<OrderPerDiemDateEntry>(),
    yearMonth: ""
  };

  const state = reactive({...initialState});

  const perDiemUpdateCallback = () => {
    loadPerDiems(currentMonth.value);
  };

  onMounted(() => {
    eventBus.$on(MessageTypes.PERDIEM_UPDATED, perDiemUpdateCallback);

    loadPerDiems(currentMonth.value);
  });

  onUnmounted(() => {
    eventBus.$off(MessageTypes.PERDIEM_UPDATED, perDiemUpdateCallback);
  });

  // The per diems are read by the order's uuid, not by its numeric id. The order prop from Order.vue is loaded
  // asynchronously and often still empty on mount, so the order is loaded once here.
  let orderLoaded: Promise<Order> | null = null;

  function loadOrder(): Promise<Order> {
    if (!orderLoaded) {
      orderLoaded = OrderServiceV2.getOrder(orderId.value).then(response => response.data);
    }
    return orderLoaded;
  }

  function loadPerDiems(yearMonth: string | null) {
    loadOrder()
      .then(order => {
        if (!order.uuid) {
          return;
        }
        return OrderServiceV2.getOrderPerDiems(order.uuid, yearMonth)
          .then(response => {
            state.perDiemOrderDates = response.data.days.map(day => toDateEntry(day, order));
            // Without a month the backend picks the most recent one
            state.yearMonth = response.data.month;
          });
      });
  }

  /**
   * Maps a day of the v2 per diems onto the entry the day view and the per diem dialog work with. A per diem
   * recorded for another order keeps that order's uuid and name only, so the dialog shows it as not editable.
   */
  function toDateEntry(day: OrderPerDiemDayDto, order: Order): OrderPerDiemDateEntry {
    const entry = new OrderPerDiemDateEntry();
    entry.date = day.date;
    entry.employees = day.employees.map(employeeDay => {
      const employeeEntry = new OrderPerDiemDateEmployeeEntry();

      const employee = new EmployeeName();
      employee.uuid = employeeDay.employee.id;
      employee.employeeId = employeeDay.employee.employeeNumber as number;
      employee.firstName = employeeDay.employee.firstName;
      employee.lastName = employeeDay.employee.lastName;
      employeeEntry.employee = employee;

      const timeSpent = new TimeSpent();
      timeSpent.minutesWork = employeeDay.workMinutes ?? 0;
      timeSpent.minutesTravel = employeeDay.travelMinutes ?? 0;
      timeSpent.minutesPassenger = employeeDay.passengerMinutes ?? 0;
      employeeEntry.timeSpent = timeSpent;

      const perDiem = employeeDay.perDiem;
      if (perDiem) {
        employeeEntry.perDiem = {
          uuid: perDiem.id,
          date: day.date,
          employee: employee,
          order: perDiem.order.id === order.uuid
            ? {id: order.id, uuid: order.uuid, name: order.name}
            : {uuid: perDiem.order.id, name: perDiem.order.name},
          catering: perDiem.catering,
          misc: perDiem.misc,
          breakfast: !!perDiem.breakfast,
          calculatedCatering: perDiem.cateringAfterBreakfast
        } as PerDiem;
      }

      return employeeEntry;
    });

    return entry;
  }

  const breadcrumbs: ComputedRef<Array<BreadCrumb>> = computed(() => {
    const breadcrumbs = [];
    breadcrumbs.push({
      text: moment(currentMonth.value, "YYYY-MM").format("MMMM YYYY"),
      disabled: !isDayView.value,
      exact: true,
      to: {path: router.app.$route.path, query: {view: "list", date: currentMonth.value}}
    });
    if (isDayView.value) {
      breadcrumbs.push({
        text: moment(date.value).format("DD. MMMM YYYY"),
        disabled: true
      });
    }

    return breadcrumbs;
  });

  const currentDateEntry: ComputedRef<OrderPerDiemDateEntry> = computed(() => {
    const find = state.perDiemOrderDates.find(dateEntry => dateEntry.date === date.value);
    if (find) {
      return find;
    } else {
      const entry = new OrderPerDiemDateEntry();
      entry.date = date.value ? date.value : moment().format("YYYY-MM-DD");
      entry.employees = new Array<OrderPerDiemDateEmployeeEntry>();

      return entry;
    }
  });

  const currentMonth: ComputedRef<string | null> = computed(() => {
    if (isValidDate(date.value)) {
      return moment(date.value, "YYYY-MM-DD").format("YYYY-MM");
    } else if (isValidMonth(date.value)) {
      return date.value;
    } else {
      return null;
    }
  });

  const isDayView: ComputedRef<boolean> = computed(() => {
    return isValidDate(date.value);
  });

  const minMonth: ComputedRef<string> = computed(() => {
    return props.order?.validFrom ? moment(props.order.validFrom).format("YYYY-MM") : "";
  });

  const maxMonth: ComputedRef<string> = computed(() => {
    return props.order?.validTo ? moment(props.order.validTo).format("YYYY-MM") : "";
  });

  function edit(item: OrderPerDiemDateEntry) {
    router.app.$router.push({query: Object.assign({}, router.app.$route.query, {date: item.date})});
  }

  // Updates the date part of the current URL
  watch(() => state.yearMonth, (newYearMonth, oldYearMonth) => {
    // Only if the date has changed by either clicking or using a new URI update the actual route
    if (newYearMonth !== oldYearMonth && newYearMonth != currentMonth.value) {
      router.app.$router.push({query: Object.assign({}, router.app.$route.query, {date: newYearMonth})});
    }
  });

  watch(() => currentMonth.value, (newYearMonth, oldYearMonth) => {
    if (newYearMonth && oldYearMonth && newYearMonth != oldYearMonth) {
      loadPerDiems(newYearMonth);
    }
  });

return { __sfc: true,props, initialState, state, perDiemUpdateCallback, orderLoaded, loadOrder, loadPerDiems, toDateEntry, breadcrumbs, currentDateEntry, currentMonth, isDayView, minMonth, maxMonth, edit, MonthPicker, SwitchView, OrderPerDiemsDate, date, isValidDate }
}

})